import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, ChevronLeft, ChevronRight, Download, Loader2, Pencil, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/kit";
import { prepPhoto, drawCollage } from "@/lib/joyCollage";
import { shareOrDownload } from "@/lib/gardenShare";

const BASE = process.env.REACT_APP_BACKEND_URL;

const TodayCard = ({ today, onSaved }) => {
  const input = useRef(null);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const pick = () => {
    if (today && !window.confirm("Replace today's joy snap with a new photo?")) return;
    input.current.click();
  };
  const upload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const blob = await prepPhoto(file);
      const form = new FormData();
      form.append("file", blob, "joy.jpg"); form.append("caption", caption.trim()); form.append("replace", today ? "true" : "false");
      await api.post("/joy-snaps", form);
      setCaption(""); toast.success(today ? "Today's snap replaced ✨" : "Today's joy snap is saved ✨"); onSaved();
    } catch (err) { toast.error(err.response ? errMsg(err) : err.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="mm-card p-5 flex flex-col sm:flex-row gap-5 items-center" data-testid="joy-today">
      {today ? <img src={`${BASE}${today.url}`} alt={today.caption || "Today's joy snap"} className="w-40 h-40 object-cover rounded-2xl bg-mm-primary/10" data-testid="joy-today-img" />
        : <div className="w-40 h-40 rounded-2xl bg-mm-primary/10 flex items-center justify-center text-5xl" aria-hidden>📷</div>}
      <div className="flex-1 w-full">
        <div className="font-semibold">{today ? "Today's joy is saved" : "What made you smile today?"}</div>
        <p className="text-sm text-mm-muted">One photo a day of something good. Location details are removed and only you can see it.</p>
        <input className="mm-input mt-3" maxLength={140} placeholder="Short caption (optional)" value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="joy-caption" />
        <input ref={input} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="joy-file-input" />
        <button className="mm-btn mt-3" onClick={pick} disabled={busy} data-testid="joy-add-btn">{busy ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}{today ? "Replace today's photo" : "Add today's photo"}</button>
      </div>
    </div>
  );
};

const SnapTile = ({ s, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [cap, setCap] = useState(s.caption);
  const save = async () => { try { await api.put(`/joy-snaps/${s.id}`, { caption: cap }); setEditing(false); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async () => { if (!window.confirm("Delete this joy snap?")) return; try { await api.delete(`/joy-snaps/${s.id}`); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="mm-card overflow-hidden" data-testid={`joy-snap-${s.id}`}>
      <img src={`${BASE}${s.url}`} alt={s.caption || `Joy snap from ${s.day}`} className="w-full aspect-square object-cover bg-mm-primary/10" loading="lazy" />
      <div className="p-3">
        <div className="text-xs text-mm-muted">{new Date(`${s.day}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
        {editing ? (
          <div className="flex gap-1 mt-1"><input className="mm-input !py-1 text-sm" maxLength={140} value={cap} onChange={(e) => setCap(e.target.value)} data-testid={`joy-caption-input-${s.id}`} /><button className="text-sm text-mm-primary font-semibold px-1" onClick={save} data-testid={`joy-caption-save-${s.id}`}>Save</button></div>
        ) : <p className="text-sm break-words [overflow-wrap:anywhere] min-h-[1.25rem]">{s.caption}</p>}
        <div className="flex gap-2 mt-1 text-mm-muted">
          <button onClick={() => setEditing((v) => !v)} aria-label="Edit caption" data-testid={`joy-edit-${s.id}`}><Pencil size={14} /></button>
          <button onClick={del} className="hover:text-mm-error" aria-label="Delete snap" data-testid={`joy-delete-${s.id}`}><Trash2 size={14} /></button>
        </div>
      </div>
    </div>
  );
};

const Collage = ({ data }) => {
  const [layout, setLayout] = useState("grid");
  const [captions, setCaptions] = useState(false);
  const [busy, setBusy] = useState(false);
  const make = async () => {
    setBusy(true);
    try {
      const blob = await drawCollage({ snaps: data.snaps.slice(0, 31), title: `My ${data.month_name} joys`, layout, captions, base: BASE });
      const res = await shareOrDownload(blob, `my-${data.month_name.toLowerCase()}-joys.png`, `My ${data.month_name} joys`);
      if (res === "downloaded") toast.success("Collage saved ✨");
    } catch (e) { toast.error("Couldn't make the collage. Please try again."); }
    finally { setBusy(false); }
  };
  return (
    <div className="mm-card p-5 flex flex-wrap items-center gap-3" data-testid="joy-collage">
      <div className="font-semibold w-full sm:w-auto sm:mr-auto">Monthly collage</div>
      {[["grid", "Tidy grid"], ["polaroid", "Polaroids"]].map(([k, l]) => <button key={k} className={`mm-chip !text-sm ${layout === k ? "mm-chip-on" : ""}`} onClick={() => setLayout(k)} data-testid={`joy-layout-${k}`}>{l}</button>)}
      <label className="text-sm flex items-center gap-2"><input type="checkbox" className="w-4 h-4" checked={captions} onChange={(e) => setCaptions(e.target.checked)} data-testid="joy-include-captions" />Include captions</label>
      <button className="mm-btn" onClick={make} disabled={busy} data-testid="joy-collage-btn">{busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}Save collage</button>
    </div>
  );
};

export default function JoySnaps() {
  const [month, setMonth] = useState(null);
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/joy-snaps", { params: month ? { month } : {} }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [month]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-mm-primary" /></div>;
  const i = data.months.indexOf(data.month), older = data.months[i + 1], newer = data.months[i - 1];
  return (
    <div className="max-w-4xl space-y-6" data-testid="joy-page">
      <PageHeader back title="Joy Snaps" subtitle="A photo a day of something good. Private to you." />
      <TodayCard today={data.today_snap} onSaved={load} />
      <div className="flex items-center justify-between">
        <h2 className="font-semibold" data-testid="joy-month-label">{data.label}</h2>
        <div className="flex gap-1">
          <button className="mm-btn-ghost !px-2" disabled={!older} onClick={() => setMonth(older)} aria-label="Previous month" data-testid="joy-prev-month"><ChevronLeft size={18} /></button>
          <button className="mm-btn-ghost !px-2" disabled={!newer} onClick={() => setMonth(newer)} aria-label="Next month" data-testid="joy-next-month"><ChevronRight size={18} /></button>
        </div>
      </div>
      {data.snaps.length ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">{data.snaps.map((s) => <SnapTile key={s.id} s={s} onChanged={load} />)}</div>
          <Collage data={data} />
        </>
      ) : <p className="text-sm text-mm-muted" data-testid="joy-month-empty">No joy snaps this month yet.</p>}
    </div>
  );
}
